import { FloatingInput } from "@/components/Inputs/FloatingInput/FloatingInput.tsx";
import { Toggle } from "@/components/Inputs/Toggle/Toggle.tsx";
import { AcceptedCards } from "@/components/Payment/AcceptedCards/AcceptedCards.tsx";
import { detectCardBrand } from "@/components/Payment/PaymentForm/detectCardBrand.ts";
import { CardBrand } from "@/components/Payment/PaymentForm/enums/CardBrand.ts";
import { PaymentFI } from "@/components/Payment/PaymentForm/enums/PaymentFI.ts";
import { getCardBrandImgURL } from "@/components/Payment/PaymentForm/getCardBrandImgURL.ts";
import { PaymentFormProps } from "@/components/Payment/PaymentForm/interfaces/PaymentFormProps.ts";
import { nameAndSurnameRuleset } from "@/helpers/form/FormRules.ts";
import { CreditCard } from "lucide-react";
import { ChangeEvent, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";

export const PaymentForm = (props: PaymentFormProps) => {
  const { control, handleSubmitPaymentForm, onSubmitPaymentForm, errors, register, watch } =
    props.paymentFormData;
  const [cardBrand, setCardBrand] = useState<CardBrand>();
  const [cardNumberFocused, setCardNumberFocused] = useState(false);
  const [expiryFocused, setExpiryFocused] = useState(false);
  const { t } = useTranslation();

  const watchSameAsBooker = watch(PaymentFI.SameAsBooker);
  const watchCardNumber = watch(PaymentFI.CardNumber);
  const watchExpiry = watch(PaymentFI.ExpiryDate);

  const handleCardNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cardBrand = detectCardBrand(value);

    if (cardBrand) setCardBrand(cardBrand);
    else setCardBrand(undefined);
  };

  const cardNumberError = errors[PaymentFI.CardNumber];
  const expiryError = errors[PaymentFI.ExpiryDate];

  const cardNumberClasses = [
    "c-floating-input",
    "cardInputContainer",
    (cardNumberFocused || watchCardNumber) && "c-floating-input--floating",
    cardNumberFocused && "c-floating-input--focused",
    cardNumberError && "c-floating-input--error",
  ]
    .filter(Boolean)
    .join(" ");

  const expiryClasses = [
    "c-floating-input",
    "expiryInputContainer",
    (expiryFocused || watchExpiry?.trim()) && "c-floating-input--floating",
    expiryFocused && "c-floating-input--focused",
    expiryError && "c-floating-input--error",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <form className="paymentForm" autoComplete="on" onSubmit={handleSubmitPaymentForm(onSubmitPaymentForm)}>
      {/* Hidden submit button — helps browsers recognise this as a submittable form for autofill */}
      <button type="submit" hidden aria-hidden="true" tabIndex={-1} />

      <div className="paymentForm__header">
        <div className="paymentForm__header-icon">
          <CreditCard size={20} />
        </div>
        <div className="paymentForm__header-text">
          <h3 className="paymentForm__title">{t("paymentForm.title")}</h3>
          <p className="paymentForm__description">{t("paymentForm.description")}</p>
        </div>
      </div>
      <Toggle
        label={t(`paymentForm.${PaymentFI.SameAsBooker}`)}
        registration={register(PaymentFI.SameAsBooker)}
        checked={!!watchSameAsBooker}
      />
      <div className="paymentFormGrid">
        {watchSameAsBooker ? null : (
          <>
            <FloatingInput
              label={t(`paymentForm.${PaymentFI.HolderFirstName}`)}
              placeholder=" "
              registration={register(PaymentFI.HolderFirstName, {
                ...nameAndSurnameRuleset,
                shouldUnregister: true,
              })}
              value={watch(PaymentFI.HolderFirstName)}
              error={errors[PaymentFI.HolderFirstName]}
              autoComplete="cc-given-name"
              required
            />
            <FloatingInput
              label={t(`paymentForm.${PaymentFI.HolderLastName}`)}
              placeholder=" "
              registration={register(PaymentFI.HolderLastName, {
                ...nameAndSurnameRuleset,
                shouldUnregister: true,
              })}
              value={watch(PaymentFI.HolderLastName)}
              error={errors[PaymentFI.HolderLastName]}
              autoComplete="cc-family-name"
              required
            />
          </>
        )}
        <div className={cardNumberClasses}>
          <Controller
            name={PaymentFI.CardNumber}
            control={control}
            shouldUnregister
            defaultValue=""
            render={({ field }) => (
              <div className="cardNumberInnerInputContainer">
                <InputMask
                  {...field}
                  mask="9999 9999 9999 9999"
                  placeholder=" "
                  id="cardNumber"
                  name="cardnumber"
                  autoComplete="cc-number"
                  maskChar={null}
                  className="c-floating-input__field"
                  onFocus={() => setCardNumberFocused(true)}
                  onBlur={(e) => {
                    field.onBlur();
                    setCardNumberFocused(false);
                    void e;
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    handleCardNumberChange(e);
                  }}
                />
                {cardBrand && (
                  <div className="cardImageContainer">
                    <img src={getCardBrandImgURL(cardBrand)} alt={cardBrand} className="cardImage" />
                  </div>
                )}
              </div>
            )}
          />
          <label className="c-floating-input__label" htmlFor="cardNumber">
            {t(`paymentForm.${PaymentFI.CardNumber}`)}
          </label>
          {cardNumberError && cardNumberError.type !== "required" && (
            <span className="c-floating-input__error">
              {cardNumberError.message && `${cardNumberError.message}`}
            </span>
          )}
        </div>
        <div className={expiryClasses}>
          <Controller
            name={PaymentFI.ExpiryDate}
            control={control}
            shouldUnregister
            defaultValue=""
            render={({ field }) => (
              <InputMask
                {...field}
                mask="99/99"
                placeholder=" "
                id="expiryDate"
                name="cc-exp"
                autoComplete="cc-exp"
                maskChar=" "
                className="c-floating-input__field"
                onFocus={() => setExpiryFocused(true)}
                onBlur={() => {
                  field.onBlur();
                  setExpiryFocused(false);
                }}
              />
            )}
          />
          <label className="c-floating-input__label" htmlFor="expiryDate">
            {t(`paymentForm.${PaymentFI.ExpiryDate}`)}
          </label>
          {expiryError && expiryError.type !== "required" && (
            <span className="c-floating-input__error">{expiryError.message && `${expiryError.message}`}</span>
          )}
        </div>
      </div>
      <div className="acceptedCardsWrap">
        <AcceptedCards />
      </div>
    </form>
  );
};
